import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import emailService from '../services/emailService';
import './Pages.css';

function EmailDetails() {
  const { emailId } = useParams();
  const navigate = useNavigate();
  const [emailData, setEmailData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEmailDetails();
  }, [emailId]);

  const fetchEmailDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await emailService.fetchEmailStatus(emailId);

      if (response.success) {
        setEmailData(response.status);
      } else {
        setError('Email not found');
      }
    } catch (err) {
      console.error('Error fetching email details:', err);
      setError('Failed to load email details');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;

      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;

      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } catch (error) {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="email-details-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading email details...</p>
        </div>
      </div>
    );
  }

  if (error || !emailData) {
    return (
      <div className="email-details-page">
        <div className="error-container">
          <span className="error-icon">⚠️</span>
          <h3>Error Loading Email</h3>
          <p>{error || 'Email not found'}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { email, isRead, readCount, firstRead, lastRead, deviceTypes } = emailData;

  return (
    <div className="email-details-page">
      <div className="page-header">
        <div>
          <button onClick={() => navigate('/')} className="btn-back">
            ← Back
          </button>
          <h1>{email.subject || '(No subject)'}</h1>
          <p className="page-subtitle">Tracking details and analytics</p>
        </div>
      </div>

      <div className="email-meta-card">
        <div className="meta-row">
          <span className="meta-label">To:</span>
          <span className="meta-value">{email.recipient}</span>
        </div>
        <div className="meta-row">
          <span className="meta-label">Sent:</span>
          <span className="meta-value">{formatTimestamp(email.sent_at)}</span>
        </div>
        <div className="meta-row">
          <span className="meta-label">Tracking ID:</span>
          <span className="meta-value tracking-id">{email.tracking_id}</span>
        </div>
        <div className="meta-row">
          <span className="meta-label">Status:</span>
          <span className={`status-badge ${isRead ? 'read' : 'unread'}`}>
            {isRead ? '✓ Read' : '○ Not Read Yet'}
          </span>
        </div>
      </div>

      <div className="analytics-grid">
        <div className="analytics-card">
          <div className="analytics-icon">📖</div>
          <div className="analytics-content">
            <div className="analytics-value">{readCount || 0}</div>
            <div className="analytics-label">Total Opens</div>
          </div>
        </div>

        <div className="analytics-card">
          <div className="analytics-icon">⏱️</div>
          <div className="analytics-content">
            <div className="analytics-value">
              {firstRead ? getTimeAgo(firstRead) : 'N/A'}
            </div>
            <div className="analytics-label">First Opened</div>
          </div>
        </div>

        <div className="analytics-card">
          <div className="analytics-icon">🕐</div>
          <div className="analytics-content">
            <div className="analytics-value">
              {lastRead ? getTimeAgo(lastRead) : 'N/A'}
            </div>
            <div className="analytics-label">Last Opened</div>
          </div>
        </div>

        <div className="analytics-card">
          <div className="analytics-icon">📱</div>
          <div className="analytics-content">
            <div className="analytics-value">
              {deviceTypes && deviceTypes.length > 0 ? deviceTypes.join(', ') : 'N/A'}
            </div>
            <div className="analytics-label">Device Types</div>
          </div>
        </div>
      </div>

      {isRead && (
        <div className="timeline-card">
          <h3>Tracking Timeline</h3>
          <div className="timeline">
            {firstRead && (
              <div className="timeline-event">
                <div className="timeline-marker first"></div>
                <div className="timeline-content">
                  <div className="timeline-title">First Opened</div>
                  <div className="timeline-time">{formatTimestamp(firstRead)}</div>
                  <div className="timeline-ago">{getTimeAgo(firstRead)}</div>
                </div>
              </div>
            )}

            {lastRead && lastRead !== firstRead && (
              <div className="timeline-event">
                <div className="timeline-marker last"></div>
                <div className="timeline-content">
                  <div className="timeline-title">Last Opened</div>
                  <div className="timeline-time">{formatTimestamp(lastRead)}</div>
                  <div className="timeline-ago">{getTimeAgo(lastRead)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!isRead && (
        <div className="not-read-card">
          <span className="not-read-icon">📭</span>
          <h3>Email Not Opened Yet</h3>
          <p>The recipient hasn't opened this email yet. Check back later for tracking data.</p>
        </div>
      )}
    </div>
  );
}

export default EmailDetails;
